import ApiServer from '@/utils/api/ApiServer.js'
import { DeckContentType } from '@/utils/Enumerations'

export default {
  fetchSummaryData: async (_, { sinceTime, untilTime, accountId, includeInternal, includeExternal, useLoadCreatedAt, myTracts = false }) => {
    if (sinceTime === undefined) {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      sinceTime = firstDayOfMonth.toISOString()
    }

    const params = {
      sinceTime,
      untilTime,
      includeInternal,
      includeExternal,
      useLoadCreatedAt: useLoadCreatedAt ?? false,
      accountId: accountId !== -1 ? accountId : undefined,
      limitToMyTracts: myTracts
    }

    const ticketSummaryUrl = ApiServer.urlFor('dashboard/ticketSummaries', { params })
    const defectsByAccountUrl = ApiServer.urlFor('dashboard/itemizedDefectSummaryByAccount', { params })

    const { data: ticketSummaries } = await ApiServer.get(ticketSummaryUrl)
    const { data: defectsByAccount } = await ApiServer.get(defectsByAccountUrl)
    return {
      ticketSummaries,
      defectsByAccount
    }
  },

  fetchVolumeSummaryData: async (_, { sinceTime, untilTime, accountId, includeInternal, includeExternal, useLoadCreatedAt, myTracts = false }) => {
    if (sinceTime === undefined) {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      sinceTime = firstDayOfMonth.toISOString()
    }

    const params = {
      sinceTime,
      untilTime,
      includeInternal,
      includeExternal,
      useLoadCreatedAt: useLoadCreatedAt ?? false,
      accountId: accountId !== -1 ? accountId : undefined,
      limitToMyTracts: myTracts
    }

    const volumeTicketSummaryUrl = ApiServer.urlFor('dashboard/volumeticketsummaries', { params })

    const { data: volumeTicketSummaries } = await ApiServer.get(volumeTicketSummaryUrl)
    return volumeTicketSummaries
  },

  fetchLandownerSummaries: async (_, { sinceTime, untilTime, accountId, includeInternal, includeExternal, useLoadCreatedAt, myTracts = false }) => {
    if (sinceTime === undefined) {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      sinceTime = firstDayOfMonth.toISOString()
    }

    const params = {
      sinceTime,
      untilTime,
      includeInternal,
      includeExternal,
      useLoadCreatedAt: useLoadCreatedAt ?? false,
      accountId: accountId !== -1 ? accountId : undefined,
      limitToMyTracts: myTracts
    }

    const landownerTicketSummaryUrl = ApiServer.urlFor('dashboard/landownerTicketSummaries', { params })

    const { data: landownerTicketSummaries } = await ApiServer.get(landownerTicketSummaryUrl)
    return { landownerTicketSummaries }
  },

  fetchByproductSummaries: async (_, { sinceTime, untilTime, accountId, includeInternal, includeExternal }) => {
    if (sinceTime === undefined) {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      sinceTime = firstDayOfMonth.toISOString()
    }

    const ticketSummaryUrl = ApiServer.urlFor('dashboard/byproductTicketSummaries', {
      params: {
        sinceTime,
        untilTime,
        includeInternal,
        includeExternal,
        accountId: accountId !== -1 ? accountId : undefined
      }
    })

    const { data: ticketSummaries } = await ApiServer.get(ticketSummaryUrl)
    return ticketSummaries
  },

  fetchItemizedDefectSummary: async (_, { sinceTime, untilTime, locationId, myTracts = false }) => {
    if (sinceTime === undefined) {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      sinceTime = firstDayOfMonth.toISOString()
    }

    const itemizedDefectSummaryUrl = ApiServer.urlFor('dashboard/itemizedDefectSummaryByDefect', {
      params: {
        sinceTime,
        untilTime,
        locationId: locationId !== -1 ? locationId : undefined,
        limitToMyTracts: myTracts
      }
    })

    const { data: itemizedDefectSummaryData } = await ApiServer.get(itemizedDefectSummaryUrl)

    return itemizedDefectSummaryData
  },

  toggleSummaryIsTons: (context, summaryKey) => {
    context.commit('toggleSummaryIsTons', summaryKey)
  },

  getYardTimes: async (_, { locationId, contentType = DeckContentType.Logs.value }) => {
    if (!locationId) {
      return []
    } else {
      const url = ApiServer.urlFor('dashboard/timeinyard', {
        params: { locationId, contentType }
      })
      const response = await ApiServer.get(url)
      return response.data
    }
  },

  fetchLogAnalysis: async (_, { locationId, sinceTime, untilTime, myTracts = false }) => {
    const url = ApiServer.urlFor('dashboard/logcostanalysis', {
      params: { locationId, sinceTime, untilTime, limitToMyTracts: myTracts }
    })
    const response = await ApiServer.get(url)
    return response.data
  },

  fetchLogQualityAnalysis: async (_, { locationId, sinceTime, untilTime, myTracts = false }) => {
    const url = ApiServer.urlFor('dashboard/logQualityAnalysis', {
      params: { locationId, sinceTime, untilTime, limitToMyTracts: myTracts }
    })
    const response = await ApiServer.get(url)
    return response.data
  },

  fetchTonsByCertification: async (_, { accountId, sinceTime, untilTime, includeInternal, includeExternal, myTracts = false }) => {
    const params = {
      sinceTime,
      untilTime,
      includeExternal,
      includeInternal,
      accountId: accountId !== -1 ? accountId : undefined,
      includeUncertified: true,
      limitToMyTracts: myTracts
    }
    const url = ApiServer.urlFor('dashboard/tonsbycertification', {
      params
    })
    const { data: tonsByCertification } = await ApiServer.get(url)
    return tonsByCertification
  },

  fetchLoadsInTransitByDestination: async () => {
    const url = ApiServer.urlFor('dashboard/loadsintransitbydestination')
    const response = await ApiServer.get(url)
    return response.data.map(d =>
      ({
        ...d,
        loadCountAndTract: d.loadCountAndTract.filter(t => t.tractName !== '')
        // TODO - JR: Filter out LYS and Transfer tickets for now.
      })).filter(d => d.loadCountAndTract.length > 0)
  },

  fetchByproductLoadsInTransitByDestination: async () => {
    const url = ApiServer.urlFor('dashboard/byproductloadsintransitbydestination')
    const response = await ApiServer.get(url)
    return response.data.map(d =>
      ({
        ...d
      })).filter(d => d.loadCountAndFromAccount.length > 0)
  },

  fetchTrucksInYard: async (_, locationId) => {
    const url = ApiServer.urlFor('dashboard/trucksinyard', {
      params: { locationId }
    })
    const response = await ApiServer.get(url)
    return response.data
  },

  fetchTicketsReadyForPickup: async (_, { limitToUnreserved = false, myTracts = false }) => {
    const url = ApiServer.urlFor('dashboard/ticketsforpickup', {
      params: { limitToUnreserved, limitToMyTracts: myTracts }
    })
    const response = await ApiServer.get(url)

    // Filter out log yard sale tickets for now.
    return response.data.filter(t => t.tract !== null)
  },

  fetchByproductTicketsReadyForPickup: async (_, { limitToUnreserved = false }) => {
    const url = ApiServer.urlFor('dashboard/byproductticketsforpickup', {
      params: { limitToUnreserved }
    })
    const response = await ApiServer.get(url)

    return response.data
  },

  fetchForecastedDaysRemaining: async () => {
    const url = ApiServer.urlFor('dashboard/standingtimberforecast')
    const response = await ApiServer.get(url)
    return response.data
  },

  fetchIncompleteTickets: async (context, contractTypes) => {
    const url = ApiServer.urlFor('dashboard/incompletetickets', {
      version: 1,
      params: {
        contractTypes
      }
    })
    const response = await ApiServer.get(url)
    context.commit('setIncompleteTickets', response.data)
    return response.data
  },

  getWidgetInteractionStatus: (context, { widgetId, x, y = undefined }) => {
    const hasTouchScreen = context.state.hasTouchScreen
    if (!hasTouchScreen & !!x) return true
    const lastX = context.state.lastWidgetTap.x
    const lastY = context.state.lastWidgetTap.y
    const lastId = context.state.lastWidgetTap.widgetId
    if (!!x && lastX === x && lastY === y && lastId === widgetId) {
      context.commit('setLastWidgetTap', { widgetId: undefined, x: undefined, y: undefined })
      return true
    } else {
      context.commit('setLastWidgetTap', { widgetId: widgetId, x: x, y: y })
      return false
    }
  },

  fetchReadyForScalingTicketsByYardInstruction: async (context, { locationId }) => {
    const url = ApiServer.urlFor('dashboard/readyforscalingbyyardinstruction', {
      params: {
        locationId: locationId
      }
    })
    const response = await ApiServer.get(url)
    return response.data
  }
}
